import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import random from 'madow-tech/helpers/random';
import Icon from 'madow-tech/components/icon';
import SpaceCard from 'madow-tech/components/space-card';
import SpaceBadge from 'madow-tech/components/space-badge';
import TypewriterSequence from 'madow-tech/components/typewriter-sequence';
import typewriter from 'madow-tech/modifiers/typewriter';
import inView from 'madow-tech/modifiers/in-view';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';

const members = [
  {
    imgId: 'marce',
    alias: 'Mars',
    nameKey: 'team.members.marce.name',
    roleKey: 'team.members.marce.role',
    githubKey: 'team.members.marce.github',
    linkedinKey: 'team.members.marce.linkedin',
    tags: [
      'team.members.marce.tag1',
      'team.members.marce.tag2',
      'team.members.marce.tag3',
    ],
  },
  {
    imgId: 'martin',
    alias: 'Martín',
    nameKey: 'team.members.martin.name',
    roleKey: 'team.members.martin.role',
    githubKey: 'team.members.martin.github',
    linkedinKey: 'team.members.martin.linkedin',
    tags: [
      'team.members.martin.tag1',
      'team.members.martin.tag2',
      'team.members.martin.tag3',
    ],
  },
  {
    imgId: 'vicky',
    alias: 'Vicky',
    nameKey: 'team.members.vicky.name',
    roleKey: 'team.members.vicky.role',
    githubKey: 'team.members.vicky.github',
    linkedinKey: 'team.members.vicky.linkedin',
    tags: ['team.members.vicky.tag1', 'team.members.vicky.tag2'],
  },
  {
    imgId: 'edgar',
    alias: 'Edgar',
    nameKey: 'team.members.edgar.name',
    roleKey: 'team.members.edgar.role',
    githubKey: 'team.members.edgar.github',
    linkedinKey: 'team.members.edgar.linkedin',
    tags: [
      'team.members.edgar.tag1',
      'team.members.edgar.tag2',
      'team.members.edgar.tag3',
    ],
  },
  {
    imgId: 'mike',
    alias: 'Mike',
    nameKey: 'team.members.mike.name',
    roleKey: 'team.members.mike.role',
    githubKey: 'team.members.mike.github',
    linkedinKey: 'team.members.mike.linkedin',
    tags: ['team.members.mike.tag1', 'team.members.mike.tag2'],
  },
  {
    imgId: 'july',
    alias: 'July',
    nameKey: 'team.members.july.name',
    roleKey: 'team.members.july.role',
    githubKey: 'team.members.july.github',
    linkedinKey: 'team.members.july.linkedin',
    tags: ['team.members.july.tag1', 'team.members.july.tag2'],
  },
  {
    imgId: 'emma',
    alias: 'Emma',
    nameKey: 'team.members.emma.name',
    roleKey: 'team.members.emma.role',
    githubKey: 'team.members.emma.github',
    linkedinKey: 'team.members.emma.linkedin',
    tags: [
      'team.members.emma.tag1',
      'team.members.emma.tag2',
      'team.members.emma.tag3',
    ],
  },
  {
    imgId: 'jun',
    alias: 'Rodrigo',
    nameKey: 'team.members.jun.name',
    roleKey: 'team.members.jun.role',
    githubKey: 'team.members.jun.github',
    linkedinKey: 'team.members.jun.linkedin',
    tags: [
      'team.members.jun.tag1',
      'team.members.jun.tag2',
      'team.members.jun.tag3',
    ],
  },
];

export default class TeamExplorer extends Component {
  @tracked hoveredIndex = -1;
  @tracked completedCards = new Set();
  @tracked typewriterActiveIndex = -1;
  hoverTimeout: number | null = null;

  @action
  onCardHover(index) {
    this.hoveredIndex = index;

    // Clear any existing timeout
    if (this.hoverTimeout !== null) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }

    // Reset typewriter active state
    this.typewriterActiveIndex = -1;

    // Delay typewriter activation by 200ms
    this.hoverTimeout = window.setTimeout(() => {
      this.typewriterActiveIndex = index;
    }, 200);
  }

  @action
  onCardLeave() {
    this.hoveredIndex = -1;
    this.typewriterActiveIndex = -1;

    // Clear timeout if user leaves before 2 seconds
    if (this.hoverTimeout !== null) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  @action
  onCardAnimationComplete(index) {
    this.completedCards.add(index);
    // Forzar re-render
    this.completedCards = new Set(this.completedCards);
  }

  isCardActive = (index) => {
    return this.hoveredIndex === index;
  };

  isTypewriterActive = (index) => {
    return this.typewriterActiveIndex === index;
  };

  isCardCompleted = (index) => {
    return this.completedCards.has(index);
  };

  <template>
    <section id="team" class="py-20 md:py-28">
      <div class="mx-auto max-w-7xl px-4">
        <div class="flex flex-col justify-between gap-4 mb-4">
          <h2 class="text-3xl md:text-4xl font-bold">{{t "team.title"}}</h2>
          <p class="text-base text-zinc-400">{{t "team.intro"}}</p>
        </div>

        <div
          class="mt-12 md:mt-16 grid min-[390px]:grid-cols-2 lg:grid-cols-4 gap-y-16 sm:gap-y-8 gap-x-4 md:gap-y-10 md:gap-x-6"
        >
          {{#each members as |m index|}}
            <div>
              <div
                class="group z-40 relative
                  {{if
                    (this.isCardActive index)
                    'active-team-card [&_.team-typewriter-container]:bg-black/50 [&_.team-typewriter-container]:opacity-100'
                  }}"
                {{on "mouseenter" (fn this.onCardHover index)}}
                {{on "mouseleave" this.onCardLeave}}
                {{on "touchstart" (fn this.onCardHover index)}}
                {{on "touchend" this.onCardLeave}}
              >
                <SpaceCard
                  @variant="v1"
                  @hasHoverEffect={{true}}
                  @title={{m.alias}}
                  @titlePosition="center"
                  @titleClass="font-semibold text-nowrap !-left-20"
                  class="!p-0 !mt-0 team-card"
                  {{inView threshold=0.2 once=true delay=(random 0 800)}}
                >
                  <div class="relative z-10">
                    <div
                      class="aspect-square bg-gradient-to-br from-mars-900/40 to-black/30 overflow-hidden"
                    >
                      <img
                        src="/img/team/{{m.imgId}}.webp"
                        alt="{{t m.nameKey}} – {{t m.roleKey}} at Madow Tech"
                        width="400"
                        height="400"
                        loading="lazy"
                        decoding="async"
                        class="w-full h-full object-cover team-member-photo"
                      />
                    </div>
                    <div
                      class="team-typewriter-container absolute inset-x-0 bottom-0 p-2 min-[480px]:p-4 sm:p-6 backdrop-blur-sm h-full place-content-end opacity-0 terminal-overlay"
                    >
                      <TypewriterSequence
                        @name={{t m.nameKey}}
                        @role={{t m.roleKey}}
                        @active={{this.isTypewriterActive index}}
                        @speed={{1}}
                        @loadingDelay={{17}}
                        @nameDelay={{27}}
                        @roleDelay={{20}}
                        @onComplete={{fn this.onCardAnimationComplete index}}
                      />
                      <div
                        class="mt-3 flex flex-wrap gap-1 sm:gap-2 badges-container
                          {{if (this.isCardCompleted index) 'ready'}}"
                      >
                        {{#each m.tags as |tagKey|}}
                          <SpaceBadge
                            @variant="tech"
                            @text={{t tagKey}}
                            @autoIcon={{true}}
                            @size="sm"
                          />
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </SpaceCard>
              </div>
              {{#if (or (t m.githubKey) (t m.linkedinKey))}}
                <div class="mt-3 flex gap-3 items-center justify-center">
                  {{#if (t m.githubKey)}}
                    <a
                      href={{t m.githubKey}}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-800/50 text-white hover:bg-[#414141]/70 hover:text-white transition-all cursor-pointer active:scale-95"
                      aria-label="GitHub profile"
                    >
                      <svg
                        class="w-5 h-5 fill-current"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
                        />
                      </svg>
                    </a>
                  {{/if}}
                  {{#if (t m.linkedinKey)}}
                    <a
                      href={{t m.linkedinKey}}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-800/50 text-white hover:bg-[#0A66C2]/70 hover:text-white transition-all cursor-pointer active:scale-95"
                      aria-label="LinkedIn profile"
                    >
                      <svg
                        class="w-5 h-5 fill-current"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                        />
                      </svg>
                    </a>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/each}}
        </div>
      </div>
    </section>
  </template>
}
